import { RunState } from "types/generated";

import { ModulePullRequestsTableRunRow } from "./gql";

export enum ModulePullRequestState {
  QUEUED = "QUEUED",
  IN_PROGRESS = "IN PROGRESS",
  FAILED = "FAILED",
  FINISHED = "FINISHED",
}

type ModulePullRequestRun = Pick<ModulePullRequestsTableRunRow, "state" | "expectFailure">;

enum RunOutcome {
  PASSED = "passed",
  FAILED = "failed",
  CANCELED = "canceled",
  SKIPPED = "skipped",
  QUEUED = "queued",
  IN_PROGRESS = "inProgress",
}

const getRunOutcome = ({ state, expectFailure }: ModulePullRequestRun): RunOutcome => {
  switch (state) {
    case RunState.Finished:
      return expectFailure ? RunOutcome.FAILED : RunOutcome.PASSED;
    case RunState.Failed:
      return expectFailure ? RunOutcome.PASSED : RunOutcome.FAILED;
    case RunState.Stopped:
    case RunState.Canceled:
    case RunState.Discarded:
      return RunOutcome.CANCELED;
    case RunState.Skipped:
      return RunOutcome.SKIPPED;
    case RunState.Queued:
    case RunState.Ready:
      return RunOutcome.QUEUED;
    case RunState.Applying:
    case RunState.Confirmed:
    case RunState.Destroying:
    case RunState.Initializing:
    case RunState.Pending:
    case RunState.PendingReview:
    case RunState.Performing:
    case RunState.Planning:
    case RunState.Preparing:
    case RunState.PreparingApply:
    case RunState.PreparingReplan:
    case RunState.ReplanRequested:
    case RunState.Unconfirmed:
    case RunState.Unknown:
      return RunOutcome.IN_PROGRESS;
  }
};

export const getModulePullRequestState = (
  runs: ModulePullRequestRun[]
): ModulePullRequestState | undefined => {
  if (runs.length === 0) {
    return undefined;
  }

  const outcomes = runs.map(getRunOutcome);

  if (outcomes.every((outcome) => outcome === RunOutcome.QUEUED)) {
    return ModulePullRequestState.QUEUED;
  }

  if (
    outcomes.some((outcome) => outcome === RunOutcome.QUEUED || outcome === RunOutcome.IN_PROGRESS)
  ) {
    return ModulePullRequestState.IN_PROGRESS;
  }

  if (
    outcomes.some((outcome) => outcome === RunOutcome.FAILED || outcome === RunOutcome.CANCELED)
  ) {
    return ModulePullRequestState.FAILED;
  }

  return ModulePullRequestState.FINISHED;
};

export const getModulePullRequestSingleRun = (
  runs: ModulePullRequestsTableRunRow[]
): ModulePullRequestsTableRunRow | undefined => (runs.length === 1 ? runs[0] : undefined);

export const isModulePullRequestExpandable = (runs: ModulePullRequestsTableRunRow[]) =>
  runs.length > 1;
